// Products Section - Products marketplace with sidebar filtering and search
// Follows the Section, Component & Manager System architecture

import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { MainLayout } from '../../components/Layout';
import { MarketplaceManager } from './managers/products-manager';
import { useManager } from '../../core';
import { ProductsLayout, ProductSearchBar, ProductGrid, ProductPagination, MarketplaceLoading } from './components';
import { RevoltSwitch } from '../../components/RevoltSwitch';
import { eventUtils } from '../../core/utils';
import { useAuth } from '../../contexts/AuthContext';
import './ProductsSection.css';

export function MarketplaceSection() {
  const { manager, isInitialized } = useManager(MarketplaceManager);
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  // Local state for immediate input responsiveness
  const [localSearchTerm, setLocalSearchTerm] = useState('');

  // Local state for current search term (triggers re-renders)
  const [currentSearchTerm, setCurrentSearchTerm] = useState('');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;
  const [searchResults, setSearchResults] = useState([]);
  const [searchTotal, setSearchTotal] = useState(0);
  const [isSearchLoading, setIsSearchLoading] = useState(false);

  // Bundle filter: toggle activates filter, switch chooses bundles vs individual
  const urlFilterBundle = searchParams.get('filterBundle') === '1';
  const urlBundleType = searchParams.get('bundleType') || 'bundles';
  const [filterBundleActive, setFilterBundleActive] = useState(urlFilterBundle);
  const [bundleShowBundles, setBundleShowBundles] = useState(urlBundleType === 'bundles');
  const [, setReloadVersion] = useState(0);

  // Get initial search term from URL parameters
  const urlQuery = searchParams.get('query') || '';

  // Initialize local state from URL on mount (only time input gets set externally)
  const hasInitializedRef = React.useRef(false);
  useEffect(() => {
    if (!hasInitializedRef.current && urlQuery) {
      hasInitializedRef.current = true;
      setLocalSearchTerm(urlQuery);
      setCurrentSearchTerm(urlQuery);
    }
  }, [urlQuery]); // Only depend on urlQuery

  // Initialize bundle filter from URL on mount
  const hasInitializedBundleRef = React.useRef(false);
  useEffect(() => {
    if (!hasInitializedBundleRef.current) {
      hasInitializedBundleRef.current = true;
      setFilterBundleActive(urlFilterBundle);
      setBundleShowBundles(urlBundleType === 'bundles');
    }
  }, [urlFilterBundle, urlBundleType]);

  // Never modify the input field content after initialization
  // The input always shows exactly what the user types, never gets overridden

  const shouldShowSearchResults = currentSearchTerm.trim().length > 0;

  // Bundle filter value for API: 'bundles' | 'individual' when active, else undefined
  const bundleFilterValue = filterBundleActive
    ? (bundleShowBundles ? 'bundles' : 'individual')
    : undefined;

  const executeSearch = useCallback(async (query, page = 1) => {
    const normalizedQuery = query.trim();
    if (!normalizedQuery) {
      setSearchResults([]);
      setSearchTotal(0);
      return;
    }

    setIsSearchLoading(true);
    const filters = bundleFilterValue ? { bundle: bundleFilterValue } : {};
    const response = await manager.searchProductsFromAPI(normalizedQuery, page, itemsPerPage, filters);
    setSearchResults(response.products || []);
    setSearchTotal(response.total || 0);
    setIsSearchLoading(false);
  }, [manager, itemsPerPage, bundleFilterValue]);

  // Function to perform actual search (called on Enter or button click)
  const performSearch = useCallback(async (query) => {
    const processedQuery = query.trim();
    setCurrentSearchTerm(processedQuery);
    setCurrentPage(1);

    if (!processedQuery) {
      setSearchResults([]);
      setSearchTotal(0);
      return;
    }

    await executeSearch(processedQuery, 1);
  }, [executeSearch]);

  // If page loads with a URL query, run backend search once initialized
  useEffect(() => {
    if (!isInitialized) return;
    if (!currentSearchTerm.trim()) return;
    setCurrentPage(1);
    executeSearch(currentSearchTerm, 1);
  }, [isInitialized, currentSearchTerm, executeSearch]);

  // Reload featured products when bundle filter changes (featured mode only)
  useEffect(() => {
    if (!isInitialized) return;
    if (shouldShowSearchResults) return; // Search mode handles its own filter via executeSearch
    let cancelled = false;
    const filters = bundleFilterValue ? { bundle: bundleFilterValue } : {};
    manager.loadContent(filters).then(() => {
      if (!cancelled) setReloadVersion((v) => v + 1);
    });
    return () => { cancelled = true; };
  }, [isInitialized, bundleFilterValue, shouldShowSearchResults, manager]);

  const featuredProducts = manager.getFeaturedProducts();
  const featuredStartIndex = (currentPage - 1) * itemsPerPage;
  const featuredEndIndex = featuredStartIndex + itemsPerPage;

  const displayedProducts = shouldShowSearchResults
    ? searchResults
    : featuredProducts.slice(featuredStartIndex, featuredEndIndex);

  const totalItems = shouldShowSearchResults ? searchTotal : featuredProducts.length;

  // Handle page changes
  const handlePageChange = React.useCallback((page) => {
    setCurrentPage(page);
    if (shouldShowSearchResults) {
      executeSearch(currentSearchTerm, page);
    }
    // Scroll to top of results when changing pages
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [shouldShowSearchResults, currentSearchTerm, executeSearch]);

  // Handle product click - navigate to product details
  const handleProductClick = useCallback((product) => {
    const params = new URLSearchParams();
    params.set('productId', product.id);
    navigate(`/marketplace/details?${params.toString()}`);
  }, [navigate]);

  // Debounced function to update URL from typing (250ms delay)
  const updateUrlFromTyping = useCallback((query) => {
    // Update URL with the exact query as entered (no processing for URL storage)
    const newParams = new URLSearchParams(searchParams);
    if (query) {
      // Store exactly what was typed (URLSearchParams will encode spaces as %20)
      newParams.set('query', query);
    } else {
      newParams.delete('query');
    }
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams]);

  // Create debounced version of URL update function (250ms delay)
  const debouncedUrlUpdateRef = React.useRef(null);

  // Update the debounced function when updateUrlFromTyping changes
  useEffect(() => {
    debouncedUrlUpdateRef.current = eventUtils.debounce(updateUrlFromTyping, 250);
  }, [updateUrlFromTyping]);

  // Helper function to call the debounced URL update
  const debouncedUrlUpdate = React.useCallback((...args) => {
    if (debouncedUrlUpdateRef.current) {
      debouncedUrlUpdateRef.current(...args);
    }
  }, []);

  // Handle immediate input changes (for responsive typing)
  const handleSearchChange = useCallback((newTerm) => {
    // Update local state immediately for responsive UI
    setLocalSearchTerm(newTerm);

    // Debounce URL update only (search happens on explicit submit)
    debouncedUrlUpdate(newTerm);
  }, [debouncedUrlUpdate]);

  // Update URL when bundle filter changes
  const updateUrlFromBundleFilter = useCallback((active, showBundles) => {
    const newParams = new URLSearchParams(searchParams);
    if (active) {
      newParams.set('filterBundle', '1');
      newParams.set('bundleType', showBundles ? 'bundles' : 'individual');
    } else {
      newParams.delete('filterBundle');
      newParams.delete('bundleType');
    }
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleFilterBundleToggle = useCallback(() => {
    const nextActive = !filterBundleActive;
    setFilterBundleActive(nextActive);
    setBundleShowBundles(true); // Default to bundles when activating
    setCurrentPage(1);
    updateUrlFromBundleFilter(nextActive, true);
  }, [filterBundleActive, updateUrlFromBundleFilter]);

  const handleBundleTypeChange = useCallback((checked) => {
    setBundleShowBundles(checked);
    setCurrentPage(1);
    updateUrlFromBundleFilter(filterBundleActive, checked);
  }, [filterBundleActive, updateUrlFromBundleFilter]);

  // Note: debounced function will clean up naturally on unmount

  if (!isInitialized) {
    return (
      <MainLayout contentClassName="main-content--products">
        <MarketplaceLoading
          title="Loading Marketplace"
          subtitle="Discovering amazing products..."
        />
      </MainLayout>
    );
  }

  return (
    <MainLayout contentClassName="main-content--products">
      <div className="products-section">
        {/* Root Layout: Sidebar + Main Content Area */}
        <ProductsLayout
          sidebar={
            <div className="products-sidebar-content">
              <div className="products-sidebar-nav">
                <h3 className="products-sidebar-section-title">Navigation</h3>
                {user && (
                  <a
                    href="/marketplace/purchased"
                    className="products-sidebar-link"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate('/marketplace/purchased');
                    }}
                  >
                    <i className="fa-solid fa-bag-shopping"></i>
                    My Purchases
                  </a>
                )}
              </div>
              <div className="products-sidebar-divider" />
              <div className="products-sidebar-filters">
                <h3 className="products-sidebar-section-title">Filters</h3>
                <button
                  type="button"
                  className={`products-filter-toggle ${filterBundleActive ? 'is-active' : ''}`}
                  onClick={handleFilterBundleToggle}
                  title={filterBundleActive ? 'Disable bundle filter' : 'Filter by bundle'}
                >
                  <i className="fa-solid fa-boxes-stacked"></i>
                  Filter by bundle
                </button>
                {filterBundleActive && (
                  <div className="products-filter-bundle-switch">
                    <RevoltSwitch
                      checked={bundleShowBundles}
                      onChange={handleBundleTypeChange}
                      label={bundleShowBundles ? 'Bundles only' : 'Individual only'}
                      className="products-filter-bundle-revolt"
                    />
                  </div>
                )}
                {/* Future: Price range slider, category filters, tag filters */}
              </div>
            </div>
          }
          content={
            <div className="products-main-content">
              {/* Title + Search Bar Row - Admin button to left of Products when admin */}
              <div className="products-controls-row">
                {user && user.role === 'admin' && (
                  <button
                    className="products-admin-btn"
                    onClick={() => navigate('/marketplace/admin')}
                    title="Manage Products (Admin)"
                  >
                    <i className="fa-solid fa-cog"></i>
                    Admin
                  </button>
                )}
                <div className="products-title-inline">Marketplace</div>
                <ProductSearchBar
                  searchTerm={localSearchTerm}
                  onSearchChange={handleSearchChange}
                  onSubmit={performSearch}
                  placeholder="Search products..."
                />
                {/* Future: Filter button on the right */}
              </div>

              {/* Pagination Controls - Only shows if more than 25 items */}
              <ProductPagination
                currentPage={currentPage}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
              />

              {/* Products Grid Area - Takes remaining vertical space */}
              <div className="products-grid-area">
                <ProductGrid
                  products={displayedProducts}
                  onProductClick={handleProductClick}
                  loading={shouldShowSearchResults ? isSearchLoading : manager.isLoading}
                  emptyMessage={
                    shouldShowSearchResults
                      ? "No products match your search"
                      : "No featured products available"
                  }
                  admin={false}
                />
              </div>
            </div>
          }
        />
      </div>
    </MainLayout>
  );
}